/**
 * ChatGPT Ads (oaiq) storefront events — items_added, checkout_started.
 *
 * contents_viewed is fired inline from sections/main-product.liquid alongside the
 * existing view_item push.
 *
 * page_viewed is NOT fired here, and must not be: verified live 2026-09-03 that the SDK
 * emits it automatically on init (a collection page, where this file fires nothing,
 * still produced one /v1/sdk/events flush). Adding a manual call would double-count.
 *
 * Loaded deferred, after constants.js and pubsub.js, because it needs subscribe()
 * and PUB_SUB_EVENTS.
 *
 * @version 1.0.0
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;
  if (!window.__oaiPixelId) return;                 // pixel not configured
  if (window.__oaiEventsWired) return;
  window.__oaiEventsWired = true;

  var DEBUG = !!window.__oaiPixelDebug;

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[oai]'].concat([].slice.call(arguments)));
  }

  function measure(name, data) {
    try {
      if (typeof window.oaiq !== 'function') return;
      window.oaiq('measure', name, data);
      log(name, data);
    } catch (e) {}
  }

  // ── Minor units ──────────────────────────────────────────────────────────────
  //
  // Shopify money fields are ALWAYS hundredths of the presentment currency. OpenAI
  // wants true ISO-4217 minor units, which only match for 2-decimal currencies.
  // INR is 2dp so the launch market is a pass-through, but Markets is enabled on
  // this store so the other two cases are reachable.

  var DP0 = { JPY:1, KRW:1, VND:1, CLP:1, ISK:1, PYG:1, UGX:1, RWF:1, BIF:1, DJF:1, GNF:1, KMF:1, VUV:1, XAF:1, XOF:1, XPF:1 };
  var DP3 = { KWD:1, BHD:1, OMR:1, JOD:1, TND:1, LYD:1, IQD:1 };

  function minor(hundredths, cur) {
    var n = Number(hundredths);
    if (!isFinite(n)) return 0;
    if (DP0[cur]) return Math.round(n / 100);
    if (DP3[cur]) return Math.round(n * 10);
    return Math.round(n);
  }

  function currency() {
    return (window.__oaiCart && window.__oaiCart.currency) || 'INR';
  }

  // Exposed for the inline contents_viewed call in sections/main-product.liquid, so the
  // exponent table lives in exactly one place. Safe to call from there: deferred scripts
  // run before DOMContentLoaded, which is when that block fires.
  window.SCOaiMinor = minor;

  // ── Cart snapshot ────────────────────────────────────────────────────────────
  //
  // variantId -> quantity, seeded server-side in theme.liquid. Used to derive the
  // quantity ACTUALLY added when the cartUpdate payload is a full cart rather than
  // just the added lines (see shape probe in onCartUpdate).

  var snapshot = {};

  (function seed() {
    var items = (window.__oaiCart && window.__oaiCart.items) || [];
    for (var i = 0; i < items.length; i++) {
      snapshot[String(items[i].id)] = Number(items[i].qty) || 0;
    }
  })();

  function toContent(item, qty, cur) {
    return {
      id: String(item.sku || item.variant_id || item.id),
      name: item.product_title || item.title || '',
      content_type: 'product',
      quantity: qty,
      amount: minor((Number(item.price) || 0) * qty, cur),
      currency: cur
    };
  }

  // ── items_added ──────────────────────────────────────────────────────────────

  // 'cart-items' is a quantity edit. 'auto-add-product*' are gifts and bundle fillers
  // the shopper never chose — counting them would inflate the event we bid on during
  // cold start. 'upsell' is published via Shopify.publish, a different bus, so it does
  // not arrive here today; listed so it works if that ever gets rewired.
  var ADD_SOURCES = { 'product-form': 1, 'upsell': 1 };

  // Normalise the three payload shapes into a list of cart lines.
  //
  // Verified live on the storefront (2026-09-03): product-form publishes the raw
  // /cart/add.js response, which is the ADDED LINE ITEM — variant_id/quantity/price at
  // the top level, no items[] and no item_count, even though `sections` was requested.
  // Its `quantity` is the line's CUMULATIVE quantity in the cart, not the amount just
  // added, so it still has to be diffed against the snapshot.
  function linesFrom(data) {
    if (Array.isArray(data.items)) return data.items;         // full cart
    if (data.variant_id || data.id) return [data];            // single added line
    return [];
  }

  function onCartUpdate(e) {
    if (!e || !ADD_SOURCES[e.source]) return;

    var cur = currency();
    var data = e.cartData || {};
    var lines = linesFrom(data);

    if (!lines.length) {
      log('items_added skipped — unrecognised payload shape', Object.keys(data));
      return;
    }

    // Every shape reports cumulative line quantities, so the added amount is always
    // quantity-minus-snapshot. Lines absent from the payload are left alone: a single
    // added line says nothing about the rest of the cart.
    var contents = [];
    var total = 0;

    for (var i = 0; i < lines.length; i++) {
      var item = lines[i];
      var id = String(item.variant_id || item.id);
      var have = Number(item.quantity) || 0;
      var qty = have - (snapshot[id] || 0);
      snapshot[id] = have;
      if (qty <= 0) continue;
      contents.push(toContent(item, qty, cur));
      total += minor((Number(item.price) || 0) * qty, cur);
    }

    if (!contents.length) {
      log('items_added skipped — no positive delta');
      return;
    }

    measure('items_added', { type: 'contents', amount: total, currency: cur, contents: contents });
  }

  // ── checkout_started ─────────────────────────────────────────────────────────
  //
  // Reuses the selector utm-capture.js exports so the two can never drift — it is the
  // same set of four checkout providers. Fallback only matters if load order changes.

  var CHECKOUT_SEL = window.SCCheckoutSel ||
    '[name="checkout"], a[href*="/checkout"], .cart__checkout, .cart-checkout, .shopifynative, ' +
    '.rzp_checkout_btn, .gokwik-checkout, #gokwik-buy-now, .snapmint-drawer-btn, ' +
    '.gokwik-snapmint-checkout, [onclick*="gokwikSdk"]';

  var lastFired = 0;

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var hit;
    try { hit = t.closest(CHECKOUT_SEL); } catch (err) { return; }
    if (!hit) return;

    // The Snapmint twins nest matching selectors and shoppers double-tap. A duplicate
    // cart sync is harmless; a duplicate oaiq event is not.
    if (Date.now() - lastFired < 2000) return;
    lastFired = Date.now();

    var cur = currency();
    var total = (window.__oaiCart && window.__oaiCart.total) || 0;

    measure('checkout_started', {
      type: 'contents',
      amount: minor(total, cur),
      currency: cur
    });
  }, true);

  // Keep the cart total fresh for checkout_started, from any cart change.
  function trackTotal(e) {
    var data = (e && e.cartData) || {};
    if (typeof data.total_price === 'number' && window.__oaiCart) {
      window.__oaiCart.total = data.total_price;
    }
  }

  var wired = false;

  function wire() {
    if (wired) return true;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    wired = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (e) {
      trackTotal(e);
      onCartUpdate(e);
    });
    log('wired');
    return true;
  }

  // Subscribe immediately — constants.js and pubsub.js are deferred ahead of this file,
  // so their globals already exist. Waiting for DOMContentLoaded would miss any
  // cartUpdate published by an inline script during parse. DOMContentLoaded is only a
  // fallback in case that load order ever changes.
  if (!wire()) {
    document.addEventListener('DOMContentLoaded', function () {
      if (!wire()) log('pubsub unavailable — items_added not wired');
    }, { once: true });
  }
})();
